import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import { Button, Grid, CssBaseline } from "@mui/material";
import { jsPDF } from "jspdf";
import html2canvas from 'html2canvas';
import { useReactToPrint } from 'react-to-print';
import Kpiresponsereport from './Kpiresponsereport';
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import reportslogo from "../../assets/images/reportslogo.png";
import { useSelector } from "react-redux";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { defaultLightTheme } from "../../assets/styled-components/themes/themelist";
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

const Kpiresponsedownloadfile = forwardRef((props, ref) => {
  const { reportdata, onRenderComplete, hospitalname, service, year, setText, text, date, setDate, autoDownload, setOpenModal, options } = props;
  const printRef = useRef();
  const { color } = useSelector((state) => state.theme);

  const accessData = useAccess();
  const access = utilities.getScreenPermissions(accessData, "ENGA009C");

  useEffect(() => {
    if (onRenderComplete) {
      onRenderComplete();
    }
  }, [onRenderComplete]);

  useEffect(() => {
    if (autoDownload && reportdata) {
      handleDownloadPDF();
      handleExcel();

    }
  }, [reportdata, autoDownload]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });
  const handleExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("KPI Report");
    worksheet.columns = Array(12).fill({ width: 15 });
    const rightLogoImage = await fetch(reportslogo).then((response) =>
      response.arrayBuffer()
    );
    const rightLogoId = workbook.addImage({
      buffer: rightLogoImage,
      extension: "png",
    });
    worksheet.addImage(rightLogoId, {
      tl: { col: 0.1, row: 0.1 },
      ext: { width: 50, height: 50 },
      editAs: "oneCell",
    });
    // Title Section
    worksheet.mergeCells("A1:J3");
    const titleCell = worksheet.getCell("B2");
    titleCell.value = `${hospitalname?.label}\n KEY PERFORMANCE INDICATOR - Repair Time Report`;
    titleCell.font = { size: 15, bold: true };
    titleCell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };

    // General Info Section
    worksheet.getCell("A4").value = "Service Standard";
    worksheet.getCell("B4").value = "Facility Management Services";
    worksheet.getCell("A4").font = { bold: true };
    worksheet.getCell("A4").alignment = { vertical: "middle", horizontal: "left" };
    worksheet.getCell("B4").alignment = { vertical: "middle", horizontal: "left" };
    worksheet.mergeCells('B4:J4');
    worksheet.mergeCells('B5:J5');
    worksheet.mergeCells('B6:J6');
    worksheet.mergeCells('B7:J7');
    worksheet.mergeCells('B8:J8');
    worksheet.mergeCells('B9:J9');
    worksheet.mergeCells('B10:J10');

    ['A1', 'B4', 'B5', 'B6', 'B7', 'B8', 'B9', 'B10',].forEach(cell => {
      worksheet.getCell(cell).border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });
    worksheet.getCell("A5").value = "Measurable Element";
    worksheet.getCell("B5").value = `Response time to equipment failure for other equipment within ${options?.value === 1 ? '2 hours' : options?.value === 2 ? '15 minutes' : 'N/A'} for ${service}.`;
    worksheet.getCell("A5").font = { bold: true };
    worksheet.getCell("A5").alignment = { vertical: "middle", horizontal: "left" };
    worksheet.getCell("B5").alignment = { vertical: "middle", horizontal: "left" };
    [worksheet.getCell("A5")].forEach(cell => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });
    // Add borders and styles to other rows (A6:B10)
    for (let i = 6; i <= 10; i++) {
      worksheet.getCell(`A${i}`).font = { bold: true };
      worksheet.getCell(`A${i}`).alignment = { vertical: "middle", horizontal: "left" };
      worksheet.getCell(`B${i}`).alignment = { vertical: "middle", horizontal: "left" };

      // Apply borders
      [worksheet.getCell(`A${i}`)].forEach(cell => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    }

    worksheet.getCell("A6").value = "Numerator";
    worksheet.getCell("B6").value = "Total cumulative number of minutes taken for response time for all incidents of equipment failure from other service areas of the Facility for the month";

    worksheet.getCell("A7").value = "Denominator";
    worksheet.getCell("B7").value = "Total numbers of calls received from other service areas of the Facility on equipment failure for the month";

    worksheet.getCell("A8").value = "Inclusion";
    worksheet.getCell("B8").value = "All calls received from individual service areas of the Facility on complaints of equipment failure and the corresponding response time for each event in the month";

    worksheet.getCell("A9").value = "Exclusion";
    worksheet.getCell("B9").value = "NA";

    worksheet.getCell("A10").value = "Target";
    worksheet.getCell("D10").value = `Target: 100% for ${options?.label} – 
${options?.value === 1 ? '2 hours' : options?.value === 2 ? '15 minutes' : ''}`;

    worksheet.mergeCells("A11:N11");

    worksheet.getCell("A11").value = `RESULTS FOR THE YEAR OF : ${year}`;
    worksheet.getCell("A11").alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };

    worksheet.getCell("A11").font = { size: 12, bold: true };
    // worksheet.getCell("A12").font = { bold: true, size: 14 }; 
    const months = [
      "Year",
      `Jan-${year}`, `Feb-${year}`, `Mar-${year}`, `Apr-${year}`, `May-${year}`, `Jun-${year}`,
      `Jul-${year}`, `Aug-${year}`, `Sep-${year}`, `Oct-${year}`, `Nov-${year}`, `Dec-${year}`, `Total`
    ];

    // Numerator Data
    const numerator = [
      "Numerator",
      `${Math.round(reportdata?.nuJan || 0)}`,
      `${Math.round(reportdata?.nuFeb || 0)}`,
      `${Math.round(reportdata?.nuMarch || 0)}`,
      `${Math.round(reportdata?.nuApril || 0)}`,
      `${Math.round(reportdata?.nuMay || 0)}`,
      `${Math.round(reportdata?.nuJune || 0)}`,
      `${Math.round(reportdata?.nuJuly || 0)}`,
      `${Math.round(reportdata?.nuAugust || 0)}`,
      `${Math.round(reportdata?.nuSept || 0)}`,
      `${Math.round(reportdata?.nuOct || 0)}`,
      `${Math.round(reportdata?.nuNov || 0)}`,
      `${Math.round(reportdata?.nuDec || 0)}`,
      `${Math.round(reportdata?.nuTotal || 0)}`
    ];

    // Denominator Data
    const denominator = [
      "Denominator",
      `${Math.round(reportdata?.deJan || 0)}`,
      `${Math.round(reportdata?.deFeb || 0)}`,
      `${Math.round(reportdata?.deMarch || 0)}`,
      `${Math.round(reportdata?.deApril || 0)}`,
      `${Math.round(reportdata?.deMay || 0)}`,
      `${Math.round(reportdata?.deJune || 0)}`,
      `${Math.round(reportdata?.deJuly || 0)}`,
      `${Math.round(reportdata?.deAugust || 0)}`,
      `${Math.round(reportdata?.deSept || 0)}`,
      `${Math.round(reportdata?.deOct || 0)}`,
      `${Math.round(reportdata?.deNov || 0)}`,
      `${Math.round(reportdata?.deDec || 0)}`,
      `${Math.round(reportdata?.deTotal || 0)}`
    ];


    // Result % Data
    const resultPercentage = [
      "Result %",
      ` ${reportdata?.perJan || "0"}`,
      ` ${reportdata?.perFeb || "0"}`,
      ` ${reportdata?.perMarch || "0"}`,
      ` ${reportdata?.perApril || "0"}`,
      ` ${reportdata?.perMay || "0"}`,
      ` ${reportdata?.perJune || "0"}`,
      ` ${reportdata?.perJuly || "0"}`,
      ` ${reportdata?.perAugust || "0"}`,
      ` ${reportdata?.perSept || "0"}`,
      ` ${reportdata?.perOct || "0"}`,
      ` ${reportdata?.perNov || "0"}`,
      ` ${reportdata?.perDec || "0"}`,
      ` ${reportdata?.perTotal || "0"}`
    ];
    /// Add Table Data to Worksheet
    worksheet.addRow(months).font = { bold: true };
    worksheet.getRow(11).alignment = { vertical: "middle", horizontal: "center" };
    // Target Compliance Rate Data (100%)
    const targetComplianceRate = [
      "Target Compliance Rate within 7 days(100%)",
      100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100
    ];
    worksheet.addRow(numerator);
    worksheet.addRow(denominator);
    worksheet.addRow(resultPercentage);
    worksheet.addRow(targetComplianceRate);
    const rows = [11, 12, 13, 14, 15];
    /// Add Table Data to Worksheet
    rows.forEach((rowIndex) => {
      const row = worksheet.getRow(rowIndex);
      row.eachCell((cell, colNumber) => {
        // Set borders for each cell
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };

        // Align specific rows to the right, including Result %
        if (rowIndex === 12 || rowIndex === 13 || rowIndex === 14 || rowIndex === 15 || rowIndex === 16) {
          cell.alignment = { vertical: "middle", horizontal: "right" }; // Align to right
        } else {
          cell.alignment = { vertical: "middle", horizontal: "center" }; // Default to center alignment
        }
      });
    });

    // Add border and alignment for the targetComplianceRate row specifically
    const targetRow = worksheet.getRow(16); // Assuming targetComplianceRate is in row 16
    targetRow.eachCell((cell) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      cell.alignment = { vertical: "middle", horizontal: "right" }; // You can change this if needed
    });

    const chartCanvas = document.createElement("canvas");
    const ctx = chartCanvas.getContext("2d");
    const filteredMonths = months.slice(1, -1);

    // Create the chart
    const chart = new Chart(ctx, {
      type: "line",
      data: {
        labels: filteredMonths,
        datasets: [
          {
            label: "Result %",
            data: resultPercentage.slice(1, -1).map((val) => parseFloat(val) || 0),  // Exclude the first and last values
            borderColor: "rgb(75, 192, 192)",
            backgroundColor: "rgba(75, 192, 192, 0.2)",
            fill: true,
          },
          {
            label: "Target",
            data: Array(12).fill(100),  // Target line at 100 for all months
            borderColor: "rgb(255, 99, 132)",
            backgroundColor: "rgba(255, 99, 132, 0.2)",
            fill: true,
          },
        ],
      },
      options: {
        responsive: false, // Important for server-side rendering
        scales: {
          y: { beginAtZero: true },
        },
      },
    });



    await new Promise((resolve) => setTimeout(resolve, 1000));
    const chartImage = chart.toBase64Image();
    const chartImageId = workbook.addImage({
      base64: chartImage.replace(/^data:image\/png;base64,/, ""),
      extension: "png",
    });


    worksheet.addImage(chartImageId, {
      tl: { col: 0, row: 17 },
      ext: { width: 400, height: 250 },
    });

    worksheet.mergeCells("A31:D35");
    worksheet.getCell("A31").value = {
      richText: [
        { text: "Analysis:\n", font: { bold: true } },
        { text: text.analysis || "", font: { bold: false } },
      ],
    };
    worksheet.getCell("A31").alignment = { vertical: "top", horizontal: "left", wrapText: true };

    // Set the border for the merged cells A31:D35
    worksheet.getCell("A31").border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
    worksheet.getCell("A31").font = { bold: true };
    worksheet.getRow(31).height = 50; // Adjust this value to fit your content
    worksheet.mergeCells("E31:J35");
    worksheet.getCell("E31").value = {
      richText: [
        { text: "Immediate Action Taken:\n", font: { bold: true } },
        { text: text.immediateAction || "", font: { bold: false } },
      ],
    }
    worksheet.getCell("E31").alignment = { vertical: "top", horizontal: "left", wrapText: true };

    // Set the border for the merged cells E31:H35
    worksheet.getCell("E31").border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
    worksheet.getCell("E31").font = { bold: true };
    worksheet.getRow(31).height = 50; // Adjust this value to fit your content


    worksheet.mergeCells("A36:D39");
    worksheet.getCell("A36").value = {
      richText: [
        { text: "Quality Improvement Activities:\n", font: { bold: true } },
        { text: text.qualityImprovement || "", font: { bold: false } },
      ],
    };

    worksheet.getCell("A36").alignment = { vertical: "top", horizontal: "left", wrapText: true };
    worksheet.getCell("A36").border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };

    worksheet.getCell("A36").font = { bold: true };
    worksheet.mergeCells("E36:F36");
    worksheet.getCell("E36").value = "Prepared by:";
    worksheet.getCell("E36").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    worksheet.getCell("E36").border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
    worksheet.getCell("E36").font = { bold: true };
    worksheet.mergeCells("G36:H36");
    worksheet.getCell("G36").value = "Reviewed by:";
    worksheet.getCell("G36").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    worksheet.getCell("G36").border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
    worksheet.getCell("G36").font = { bold: true };
    worksheet.mergeCells("I36:J36");
    worksheet.getCell("I36").value = "Approved by:";
    worksheet.getCell("I36").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    worksheet.getCell("I36").border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
    worksheet.getCell("J36").border = {
      top: { style: 'thin' },
      right: { style: 'thin' }, // Added the right border for the "Approved by:" section
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };

    worksheet.getCell("I36").font = { bold: true };
    // Adjust the text in cells for Name, Designation, and Date (under each section)
    worksheet.mergeCells("E37:F37");
    worksheet.getCell("E37").value = {
      richText: [
        { text: "Name:", font: { bold: true } },
        { text: date.name || " ", font: { bold: false } },
      ],
    };
    worksheet.getCell("E37").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    worksheet.getCell("E37").border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

    worksheet.mergeCells("E38:F38");
    worksheet.getCell("E38").value = {
      richText: [
        { text: "Designation:", font: { bold: true } },
        { text: date.designation || " ", font: { bold: false } },
      ],
    };
    worksheet.getCell("E38").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    worksheet.getCell("E38").border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

    worksheet.mergeCells("E39:F39");
    worksheet.getCell("E39").value = {
      richText: [
        { text: "Date:", font: { bold: true } },
        { text: date.date || " ", font: { bold: false } },
      ],
    };
    worksheet.getCell("E39").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    worksheet.getCell("E39").border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

    // For Reviewed by:
    worksheet.mergeCells("G37:H37");
    worksheet.getCell("G37").value = {
      richText: [
        { text: "Name:", font: { bold: true } },
        { text: date.names || " ", font: { bold: false } },
      ],
    };
    worksheet.getCell("G37").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    worksheet.getCell("G37").border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

    worksheet.mergeCells("G38:H38");
    worksheet.getCell("G38").value = {
      richText: [
        { text: "Designation:", font: { bold: true } },
        { text: date.designations || " ", font: { bold: false } },
      ],
    };
    worksheet.getCell("G38").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    worksheet.getCell("G38").border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

    worksheet.mergeCells("G39:H39");
    worksheet.getCell("G39").value = {
      richText: [
        { text: "Date:", font: { bold: true } },
        { text: date.dates || " ", font: { bold: false } },
      ],
    };
    worksheet.getCell("G39").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    worksheet.getCell("G39").border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

    // For Approved by:
    worksheet.mergeCells("I37:J37");
    worksheet.getCell("I37").value = {
      richText: [
        { text: "Name:", font: { bold: true } },
        { text: date.nam || " ", font: { bold: false } },
      ],
    };
    worksheet.getCell("I37").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    worksheet.getCell("I37").border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

    worksheet.mergeCells("I38:J38");
    worksheet.getCell("I38").value = {
      richText: [
        { text: "Designation:", font: { bold: true } },
        { text: date.designatio || " ", font: { bold: false } },
      ],
    };
    worksheet.getCell("I38").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    worksheet.getCell("I38").border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

    worksheet.mergeCells("I39:J39");
    worksheet.getCell("I39").value = {
      richText: [
        { text: "Date:", font: { bold: true } },
        { text: date.dat || " ", font: { bold: false } },
      ],
    };
    worksheet.getCell("I39").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    worksheet.getCell("I39").border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };



    try {
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), "KPI Response_Report.xlsx");
      console.log("File saved successfully");
    } catch (error) {
      console.error("Error while saving Excel file:", error);
    }

  };

  const handleDownloadPDF = () => {
    html2canvas(printRef.current, {
      scale: 2,
      useCORS: true,
    }).then(canvas => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = 200;
      const pdfHeight = 297;


      const imgWidth = canvas.width * 0.264583;
      const imgHeight = canvas.height * 0.264583;
      const scale = pdfWidth / imgWidth;
      const width = imgWidth * scale;
      const height = imgHeight * scale;
      const finalHeight = height > pdfHeight ? pdfHeight : height;
      const x = 5;
      const y = (pdfHeight - finalHeight) / 2;

      pdf.addImage(imgData, 'PNG', x, y, width, finalHeight);
      pdf.save("KPI Reponse Time Report.pdf");
    }).catch(error => {
      console.error("Error generating PDF:", error);

    })
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  useImperativeHandle(ref, () => ({
    handleDownloadPDF,
    handleExcel
  }));

  return (
    <>
      <MuiThemeProvider
        theme={{
          ...defaultLightTheme,
          palette: {
            ...defaultLightTheme.palette,
            background: {
              default: color !== "default" && "rgba(17, 17, 17, 1)",
            },
          },
        }}
      >
        <CssBaseline />

        <Grid container justifyContent="flex-end">
          {access?.canPrint && (<>
            <Grid item>
              <Button variant="contained" onClick={handlePrint} style={{ marginRight: '8px' }}>Print</Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={handleDownloadPDF}
                startIcon={<DownloadIcon />}
                style={{ marginRight: '8px' }}
              >
                PDF
              </Button>
            </Grid>
          </>)}
          <Grid item>
            <Button
              variant="contained"
              onClick={handleExcel}
              startIcon={<InsertDriveFileIcon />}
              style={{ marginRight: '8px' }}
            >
              XLSX
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={handleClose}>Close</Button>
          </Grid>
        </Grid>


      </MuiThemeProvider>
      <Kpiresponsereport ref={printRef} setText={setText}
        text={text}
        setDate={setDate}
        date={date} reportdata={reportdata} options={options} hospitalname={hospitalname} service={service} year={year} />
    </>
  );
});

export default Kpiresponsedownloadfile;
